import type { CandidateRouteInfo, ICurrentRouteInfo } from 'feature-router'
import type { IFetchApi, Experiments, ILogger, RouterFetchData } from '@wix/thunderbolt-symbols'
import type {
	IDynamicPagesResponseHandler,
	IDynamicPagesWarmupData,
	IPermissionsHandlerProvider,
	RouterPagesSeoToIdMap,
	UnresolvedRouteInfo,
} from './types'
import { name } from './symbols'
import type { IWarmupDataProvider } from 'feature-warmup-data'
import type { RouterFetchAPI } from 'feature-router-fetch'
import { RouterFetchRequestTypes } from 'feature-router-fetch'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

export const errorPagesIds = {
	FORBIDDEN: '_403_dp',
	NOT_FOUND: '_404_dp',
	INTERNAL_ERROR: '_500_dp',
	UKNOWN_ERROR: '_uknown_error_dp',
}

const FORBIDDEN = 403
const NOT_FOUND = 404

export const getErrorPageId = (
	{ status, page, redirectUrl }: { status: number; page: string; redirectUrl: string },
	exception: string | undefined
): string | null => {
	if (exception) {
		return errorPagesIds.INTERNAL_ERROR
	}

	if (status === FORBIDDEN) {
		return errorPagesIds.FORBIDDEN
	}

	if (status === NOT_FOUND) {
		return errorPagesIds.NOT_FOUND
	}

	if (!page && !redirectUrl) {
		return errorPagesIds.UKNOWN_ERROR
	}

	return null
}

const getPathParts = (relativeUrl: string): Array<string> => relativeUrl.replace('./', '').split('/')

export const isExternalUrl = (url: string): boolean => /(^https?)|(^data)|(^blob)|(^\/\/)/.test(url)

export const getRelativeUrl = (redirectUrl: string, routerPrefix: string): string =>
	/^\/(.*)/.test(redirectUrl) ? `.${redirectUrl}` : `./${routerPrefix}/${redirectUrl}`

export const getRouterSuffix = (relativeEncodedUrl: string): string => {
	// We need to use relativeEncodedUrl because router suffix can contain any charecter,
	// and in case of suffix with slash we have to use encoded value to prevent splitting the suffix to two url parts
	// e.g http://mysite.com/router-1/good%2Fevil -> relativeUrl will be './router-1/good/evil'
	// and relativeEncodedUrl will be './router-1/good%2Fevil'
	const remainingPathParts = getPathParts(relativeEncodedUrl).slice(1)

	return `/${remainingPathParts.join('/')}`
}

export const getRouterPrefix = (relativeUrl: string): string => getPathParts(relativeUrl)[0]

export const isUnresolvedRouteInfo = (routeInfo: Partial<CandidateRouteInfo>): routeInfo is UnresolvedRouteInfo =>
	!routeInfo.pageId && !!routeInfo.relativeUrl && !!routeInfo.parsedUrl && !!routeInfo.relativeEncodedUrl

const getWarmupDynamicRouteInfo = (
	currentRouteInfo: ICurrentRouteInfo,
	experiments: Experiments,
	warmupDataProvider: IWarmupDataProvider
) => {
	const currentRoute = currentRouteInfo.getCurrentRouteInfo()
	if (!isExperimentOpen(experiments, 'specs.thunderbolt.dynamicPagesWarmupData2') || currentRoute) {
		return null
	}

	// we don't want to wait for it, only use it if we already have it (i.e. documentReady)
	return warmupDataProvider.getWarmupData<IDynamicPagesWarmupData>(name, { timeout: 0 })
}

export const getCandidateRouteInfo = async (
	routeInfo: UnresolvedRouteInfo,
	routerPrefix: string,
	routerFetchData: RouterFetchData,
	routerPagesSeoToIdMap: RouterPagesSeoToIdMap,
	staticRoutedPageId: string,
	dynamicPageIdOverride: string | undefined,
	currentRouteInfo: ICurrentRouteInfo,
	experiments: Experiments,
	warmupDataProvider: IWarmupDataProvider
) => {
	if (!routerFetchData) {
		if (routerPagesSeoToIdMap[routerPrefix]) {
			return {
				...routeInfo,
				pageId: errorPagesIds.NOT_FOUND,
			}
		}
		return routeInfo
	}

	if (staticRoutedPageId && !dynamicPageIdOverride) {
		return {
			...routeInfo,
			pageId: staticRoutedPageId,
		}
	}

	const warmupDynamicRouteInfo = await getWarmupDynamicRouteInfo(currentRouteInfo, experiments, warmupDataProvider)
	if (warmupDynamicRouteInfo) {
		return {
			...routeInfo,
			...warmupDynamicRouteInfo,
		}
	}
}

export const customRouterFetchRouteInfo = async (
	routeInfo: UnresolvedRouteInfo,
	{ tryToGetCachableFetchParams }: RouterFetchAPI,
	{ handleResponse }: IDynamicPagesResponseHandler,
	{ envFetch }: IFetchApi,
	routerFetchData: RouterFetchData,
	permissionsHandlerProvider: IPermissionsHandlerProvider,
	dynamicPageIdOverride: string | undefined,
	logger: ILogger
) => {
	logger.interactionStarted('custom_router_fetch_route_info')
	const routerSuffix = getRouterSuffix(routeInfo.relativeEncodedUrl)
	const queryParams = routeInfo.parsedUrl.search
	const { url, options } = await tryToGetCachableFetchParams(RouterFetchRequestTypes.PAGES, routerFetchData, {
		routerSuffix,
		queryParams,
		dynamicPageIdOverride,
	})

	const routeInfoFromResponsePromise = handleResponse(envFetch(url, options), routeInfo)
	return permissionsHandlerProvider
		.getHandler()
		.handle(routeInfoFromResponsePromise, routeInfo)
		.then((candidateRouteInfo) => {
			logger.interactionEnded('custom_router_fetch_route_info')
			return candidateRouteInfo
		})
}

export const getSuccessResponse = (routeInfo: Partial<CandidateRouteInfo>, data: any): Partial<CandidateRouteInfo> => {
	const { page: pageId, data: pageData, head: pageHeadData, tpaInnerRoute, publicData } = data.result

	return {
		...routeInfo,
		pageId,
		dynamicRouteData: {
			pageData,
			pageHeadData,
			publicData,
			tpaInnerRoute,
		},
	}
}
